"use client";

import { motion } from "framer-motion";
import { useEffect, useState } from "react";

/* ======================
   DNA DATA (base-pairs)
====================== */
const BASE_PAIRS = [
  { left: "A", right: "T" },
  { left: "G", right: "C" },
  { left: "T", right: "A" },
  { left: "C", right: "G" },
];

export default function LogoDNAHeader() {
  const [dark, setDark] = useState(true);

  // Detect theme (safe)
  useEffect(() => {
    if (typeof document !== "undefined") {
      setDark(document.documentElement.classList.contains("dark"));
    }
  }, []);

  return (
    <div className="relative w-8 h-8 flex-shrink-0">

      {/* Ambient glow */}
      <div
        className={`
          absolute inset-0 rounded-lg blur-md
          ${dark ? "bg-cyan-400/20" : "bg-indigo-400/20"}
        `}
      />

      {/* ================= COMPUTER FRAME ================= */}
      <svg viewBox="0 0 80 80" className="relative w-full h-full">
        <defs>
          <linearGradient id="frameGradMini" x1="0" y1="0" x2="1" y2="1">
            <stop stopColor={dark ? "#22d3ee" : "#4f46e5"} />
            <stop offset="1" stopColor={dark ? "#a855f7" : "#7c3aed"} />
          </linearGradient>
        </defs>

        {/* Screen */}
        <rect
          x="14"
          y="14"
          rx="7"
          width="52"
          height="36"
          fill={dark ? "#020617" : "#ffffff"}
          stroke="url(#frameGradMini)"
          strokeWidth="2"
        />

        {/* Screen inner glow */}
        <rect
          x="18"
          y="18"
          rx="5"
          width="44"
          height="28"
          fill={
            dark
              ? "rgba(34,211,238,0.05)"
              : "rgba(99,102,241,0.08)"
          }
        />

        {/* Stand */}
        <rect
          x="37"
          y="54"
          width="6"
          height="5"
          rx="2"
          fill={dark ? "#22d3ee" : "#6366f1"}
        />
        <rect
          x="28"
          y="59"
          width="24"
          height="4"
          rx="2"
          fill={dark ? "#22d3ee" : "#6366f1"}
        />
      </svg>

      {/* ================= DNA (ROTATING & DATA-DRIVEN) ================= */}
      <motion.svg
        viewBox="0 0 80 80"
        className="absolute inset-0"
        style={{ originX: "50%", originY: "50%" }}
        animate={{ rotate: [-3, 3, -3] }}
        transition={{
          duration: 8,
          ease: "easeInOut",
          repeat: Infinity,
        }}
      >
        {/* DNA strands */}
        <motion.path
          d="M40 20 C33 26 33 36 40 42"
          stroke={dark ? "#22d3ee" : "#4f46e5"}
          strokeWidth="1.8"
          fill="none"
          animate={{ opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 3.5, repeat: Infinity }}
        />

        <motion.path
          d="M40 20 C47 26 47 36 40 42"
          stroke={dark ? "#a855f7" : "#7c3aed"}
          strokeWidth="1.8"
          fill="none"
          animate={{ opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 3.5, repeat: Infinity, delay: 0.4 }}
        />

        {/* Base pairs (data-driven) */}
        {BASE_PAIRS.map((pair, i) => {
          const y = 22 + i * 5;

          return (
            <motion.g key={i}>
              {/* Connecting line */}
              <motion.line
                x1={36}
                x2={44}
                y1={y}
                y2={y}
                stroke={dark ? "#22d3ee" : "#6366f1"}
                strokeWidth="1.4"
                animate={{ opacity: [0.3, 0.9, 0.3] }}
                transition={{
                  duration: 2.8,
                  repeat: Infinity,
                  delay: i * 0.35,
                }}
              />

              {/* Left base */}
              <text
                x={33}
                y={y + 2}
                fontSize="4"
                fill={dark ? "#67e8f9" : "#4f46e5"}
                textAnchor="end"
                opacity={0.85}
              >
                {pair.left}
              </text>

              {/* Right base */}
              <text
                x={47}
                y={y + 2}
                fontSize="4"
                fill={dark ? "#d8b4fe" : "#7c3aed"}
                textAnchor="start"
                opacity={0.85}
              >
                {pair.right}
              </text>
            </motion.g>
          );
        })}
      </motion.svg>
    </div>
  );
}
